import React, { useState, useEffect } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

// Initialize Highcharts modules
let modulesLoaded = false;

const loadHighchartsModules = async () => {
  if (!modulesLoaded) {
    try {
      const HighchartsMore = await import("highcharts/highcharts-more");
      const SolidGauge = await import("highcharts/modules/solid-gauge");

      if (HighchartsMore.default) HighchartsMore.default(Highcharts);
      if (SolidGauge.default) SolidGauge.default(Highcharts);

      modulesLoaded = true;
    } catch (error) {
      console.error("Error loading Highcharts modules:", error);
    }
  }
};

const API_BASE_URL = 'https://apps.genus.in/GenOpsAPI/api'; // ✅ production URL
//const API_BASE_URL = 'http://localhost:5134/api';

const GaugeCard = ({ title, value, color }) => {
  const options = {
    chart: { type: "solidgauge", height: 180 },
    title: { text: title, style: { fontSize: "16px", fontWeight: "600" } },
    pane: {
      startAngle: -120,
      endAngle: 120,
      background: {
        outerRadius: "100%",
        innerRadius: "80%",
        shape: "arc",
        backgroundColor: "#e9ecef",
      },
    },
    tooltip: { enabled: false },
    yAxis: {
      min: 0,
      max: 100,
      stops: [[1, color]],
      lineWidth: 0,
      tickWidth: 0,
      labels: { enabled: false },
    },
    plotOptions: {
      solidgauge: {
        dataLabels: {
          y: -10,
          borderWidth: 0,
          useHTML: true,
          format: `<div style="text-align:center"><span style="font-size:28px;font-weight:700">${value}%</span></div>`,
        },
      },
    },
    series: [{ name: title, data: [{ y: value }] }],
  };

  return (
    <div style={{
      background: 'white',
      padding: '25px',
      borderRadius: '20px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      border: '1px solid rgba(0, 0, 0, 0.05)',
    }}>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </div>
  );
};

// ✅ Fallback shift windows — used ONLY when a line has no ShiftTimingId
// saved yet (e.g. older records from before this feature existed).
// Once a line has a saved timing, these are never used for it.
const FALLBACK_SHIFT_WINDOW = {
  A: { start: '08:00', end: '16:30' },
  B: { start: '20:00', end: '04:30' },
};

const Dashboard = () => {
  const [modulesReady, setModulesReady] = useState(false);
  const [dashboardParams, setDashboardParams] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data states
  const [hourlyData, setHourlyData] = useState([]);
  const [gapsData, setGapsData] = useState([]);
  const [savedDowntimes, setSavedDowntimes] = useState([]);
  const [minorStoppages, setMinorStoppages] = useState([]);
  const [majorStoppages, setMajorStoppages] = useState([]);
  const [targetData, setTargetData] = useState(null);

  // ✅ NEW — the line's actual selected shift timing (from TabGenOpsDesc /
  // GenOpsShiftTimings via savedproductiondetails/range). Falls back to a
  // generic window only if this specific line/date never had one saved.
  const [shiftTiming, setShiftTiming] = useState(null); // { shiftTimingId, startTime, endTime }

  // Modal state for stoppages
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState({ title: '', data: [] });

  useEffect(() => {
    loadHighchartsModules().then(() => setModulesReady(true));

    const params = sessionStorage.getItem('dashboardParams');
    if (params) {
      const parsedParams = JSON.parse(params);
      setDashboardParams(parsedParams);
      fetchAllData(parsedParams);
    } else {
      setError('Select parameters from Home Page to view Dashboard');
      setLoading(false);
    }
  }, []);

  const fetchAllData = async (params) => {
    setLoading(true);
    setError(null);

    const fetchOptions = {
      credentials: 'include',
      headers: { 'accept': '*/*' }
    };

    try {
      // Fetch hourly production data
      const hourlyResponse = await fetch(
        `${API_BASE_URL}/Hourly/range?serverId=${params.plantId}&projCode=${params.projectId}&lineNumber=${params.lineNo}&shift=${params.shift}&startDate=${params.startDate}&endDate=${params.endDate}&area=${params.area}`,
        { ...fetchOptions }
      );
      const hourlyJson = await hourlyResponse.json();
      setHourlyData(hourlyJson);

      // Fetch gaps data (major stoppages - 5+ minutes)
      const gapsResponse = await fetch(
        `${API_BASE_URL}/EntryFrom/gaps/range?serverId=${params.plantId}&projCode=${params.projectId}&lineNumber=${params.lineNo}&shift=${params.shift}&startDate=${params.startDate}&endDate=${params.endDate}&area=${params.area}`,
        { ...fetchOptions }
      );
      const gapsJson = await gapsResponse.json();
      setGapsData(gapsJson);
      setMajorStoppages(gapsJson);

      // Fetch minor stoppages (2+ minutes)
      const minorResponse = await fetch(
        `${API_BASE_URL}/EntryFrom/gaps/range?serverId=${params.plantId}&projCode=${params.projectId}&lineNumber=${params.lineNo}&shift=${params.shift}&startDate=${params.startDate}&endDate=${params.endDate}&area=${params.area}&minGapMinutes=2`,
        { ...fetchOptions }
      );
      const minorJson = await minorResponse.json();
      const filteredMinor = minorJson.filter(
        item => item.gapMinutes >= 2 && item.gapMinutes < 5
      );
      setMinorStoppages(filteredMinor);

      // Fetch saved production details for downtime reasons + shift timing
      const savedResponse = await fetch(
        `${API_BASE_URL}/EntryFrom/savedproductiondetails/range?serverId=${params.plantId}&shift=${params.shift}&area=${params.area}&projCode=${params.projectId}&lineNo=${params.lineNo}&startDate=${params.startDate}&endDate=${params.endDate}`,
        { ...fetchOptions }
      );
      const savedJson = await savedResponse.json();
      if (Array.isArray(savedJson) && savedJson.length > 0) {
        setSavedDowntimes(savedJson[0].downtimes || []);
        setTargetData(savedJson[0].target || null);

        // ✅ NEW — pick up the shift timing this line actually used that day
        if (savedJson[0].shiftStartTime && savedJson[0].shiftEndTime) {
          setShiftTiming({
            shiftTimingId: savedJson[0].shiftTimingId,
            startTime: savedJson[0].shiftStartTime,
            endTime: savedJson[0].shiftEndTime,
          });
        } else {
          setShiftTiming(null); // no timing saved for this line/date yet
        }
      }

    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────────────────
  // ✅ NEW — resolves the actual shift window to plot: the line's saved
  // timing if present, otherwise a generic fallback window for that
  // shift letter (only relevant for old records with no saved timing).
  // ─────────────────────────────────────────────────────────
  const getShiftWindow = () => {
    if (shiftTiming?.startTime && shiftTiming?.endTime) {
      return { start: shiftTiming.startTime, end: shiftTiming.endTime };
    }
    const shiftLetter = (dashboardParams?.shift || 'A').toUpperCase();
    return FALLBACK_SHIFT_WINDOW[shiftLetter] || FALLBACK_SHIFT_WINDOW.A;
  };

  // Format time for display — unwraps values >= 24 back to real clock time
  // (needed for night shifts that are represented on a continuous >24 scale
  // internally so segments sort correctly across midnight).
  const formatTimeLabel = (timeValue) => {
    let normalizedTime = timeValue >= 24 ? timeValue - 24 : timeValue;
    let hour = Math.floor(normalizedTime);
    const minutes = Math.round((normalizedTime - hour) * 60);

    if (hour >= 24) hour -= 24;
    if (hour < 0) hour += 24;

    return `${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  const generateTimelineData = () => {
    const { start: shiftStartStr, end: shiftEndStr } = getShiftWindow();

    const [ssH, ssM] = shiftStartStr.split(':').map(Number);
    const [seH, seM] = shiftEndStr.split(':').map(Number);
    const shiftStartDec = ssH + ssM / 60;
    const shiftEndDecRaw = seH + seM / 60;
    const crossesMidnight = shiftEndDecRaw <= shiftStartDec;
    const shiftEndDec = crossesMidnight ? shiftEndDecRaw + 24 : shiftEndDecRaw;
    const shiftStartFloor = Math.floor(shiftStartDec);

    // ✅ Normalizes a raw clock hour (0-23) onto a continuous scale so a
    // shift crossing midnight sorts correctly, using the LINE'S ACTUAL
    // shift start — not a hardcoded 20:00 boundary as before.
    const normalizeHour = (hour) => {
      if (!crossesMidnight) return hour;
      return hour >= shiftStartFloor ? hour - shiftStartFloor : hour + (24 - shiftStartFloor);
    };

    // Parse a "HH:MM - HH:MM" style time and normalize it onto the same
    // continuous scale as above, for comparing against segment boundaries.
    const parseTimeNormalized = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      let h = hours + minutes / 60;
      if (crossesMidnight && h < shiftStartDec) {
        h += 24;
      }
      return h;
    };

    const hourlyMap = new Map();
    // ✅ Now includes EVERY hour the API returned, including zero-production
    // hours — previously only hours with production > 0 were kept, which
    // silently dropped downtime hours from the timeline entirely.
    (hourlyData || []).forEach((item) => {
      const hourWindow = new Date(item.hourWindow);
      const hour = hourWindow.getHours();
      hourlyMap.set(hour, {
        production: item.totalCount || 0,
        status: 'running'
      });
    });

    // ✅ Build the FULL list of hours spanning the selected shift window,
    // regardless of which hours have production data. This is the core
    // fix for requirement #4: the x-axis now always reflects the selected
    // shift's start-to-end window, not just whatever hours had output.
    const startHourInt = Math.floor(shiftStartDec);
    const endHourInt = Math.ceil(shiftEndDec);
    const hours = [];
    for (let h = startHourInt; h < endHourInt; h++) {
      hours.push(((h % 24) + 24) % 24);
    }
    if (hours.length === 0) return [];

    // Parse all gaps and create gap intervals with normalization
    const gapIntervals = (gapsData || []).map(gap => {
      const [startTime, endTime] = gap.timeRange.split(' - ');
      const start = parseTimeNormalized(startTime);
      const end = parseTimeNormalized(endTime);

      const savedDowntime = savedDowntimes.find(dt => {
        const savedStart = parseTimeNormalized(dt.gapStartTime);
        const savedEnd = parseTimeNormalized(dt.gapEndTime);
        return Math.abs(savedStart - start) < 0.2 && Math.abs(savedEnd - end) < 0.2;
      });

      return {
        start,
        end,
        status: savedDowntime
          ? (savedDowntime.category === 'Planned' ? 'planned' : 'unplanned')
          : 'downtime',
        reason: savedDowntime ? savedDowntime.reason : 'Unclassified',
        gapMinutes: gap.gapMinutes
      };
    }).sort((a, b) => a.start - b.start);

    const segments = [];

    for (const hour of hours) {
      // ✅ Default to zero production if the API never returned this hour
      // at all, instead of skipping it — keeps the timeline continuous.
      const hourData = hourlyMap.get(hour) || { production: 0, status: 'running' };

      const hourStart = (crossesMidnight && hour < shiftStartDec) ? hour + 24 : hour;
      const hourEnd = hourStart + 1;
      const breakpoints = [hourStart];

      gapIntervals.forEach(gap => {
        if (gap.start > hourStart && gap.start < hourEnd) {
          breakpoints.push(gap.start);
        }
        if (gap.end > hourStart && gap.end < hourEnd) {
          breakpoints.push(gap.end);
        }
      });

      breakpoints.push(hourEnd);

      const uniqueBreakpoints = [...new Set(breakpoints)].sort((a, b) => a - b);

      const hourSegments = [];
      let totalSegmentProduction = 0;

      for (let i = 0; i < uniqueBreakpoints.length - 1; i++) {
        const segmentStart = uniqueBreakpoints[i];
        const segmentEnd = uniqueBreakpoints[i + 1];
        const segmentDuration = segmentEnd - segmentStart;

        let segmentStatus = 'running';
        let segmentReason = null;
        let segmentGapMinutes = 0;

        for (const gap of gapIntervals) {
          if (segmentStart >= gap.start && segmentEnd <= gap.end) {
            segmentStatus = gap.status;
            segmentReason = gap.reason;
            segmentGapMinutes = gap.gapMinutes;
            break;
          }
        }

        const segmentProduction = segmentStatus === 'running'
          ? hourData.production * segmentDuration
          : 0;

        hourSegments.push({
          start: segmentStart,
          end: segmentEnd,
          status: segmentStatus,
          production: segmentProduction,
          reason: segmentReason,
          gapMinutes: segmentGapMinutes
        });

        totalSegmentProduction += segmentProduction;
      }

      let roundedTotal = 0;
      let lastRunningIndex = -1;

      hourSegments.forEach((seg, idx) => {
        if (seg.status === 'running') {
          lastRunningIndex = idx;
        }
      });

      hourSegments.forEach((seg, idx) => {
        if (idx === lastRunningIndex) {
          seg.production = hourData.production - roundedTotal;
        } else {
          seg.production = Math.round(seg.production);
          roundedTotal += seg.production;
        }
      });

      segments.push(...hourSegments);
    }

    return segments;
  };

  const timelineData = generateTimelineData();

  const getDowntimeEvents = () => {
    if (!gapsData.length) return [];

    const parseTime = (timeStr) => {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours + minutes / 60;
    };

    return gapsData.map(gap => {
      const [startTime, endTime] = gap.timeRange.split(' - ');

      const saved = savedDowntimes.find(dt => {
        const s1 = parseTime(dt.gapStartTime);
        const e1 = parseTime(dt.gapEndTime);
        const s2 = parseTime(startTime);
        const e2 = parseTime(endTime);
        return Math.abs(s1 - s2) < 0.2 && Math.abs(e1 - e2) < 0.2;
      });

      const status = saved
        ? (saved.category === 'Planned' ? 'planned' : 'unplanned')
        : 'downtime';

      const reason = saved?.reason || 'Unclassified';

      return {
        timeRange: `${startTime} - ${endTime}`,
        durationMin: Math.round(gap.gapMinutes),
        status: status,
        reason: reason,
        displayText: status === 'downtime' ? 'Downtime' : (status === 'planned' ? 'Planned' : 'Unplanned')
      };
    });
  };

  // Calculate cumulative production
  let cumulativeProduction = 0;
  const cumulativeData = timelineData.map(segment => {
    cumulativeProduction += segment.production;
    return cumulativeProduction;
  });

  // Calculate cumulative target
  const targetLineData = targetData && timelineData.length > 0 ? (() => {
    const totalSegments = timelineData.length;
    const targetPerSegment = targetData / totalSegments;

    return timelineData.map((segment, index) => {
      return Math.round(targetPerSegment * (index + 1));
    });
  })() : null;

  // Get time labels
  const timeLabels = timelineData.map(segment => formatTimeLabel(segment.start));

  // Find max value for chart scaling
  const maxValue = Math.max(...cumulativeData, ...(targetLineData || [0]));

  // Create background color data - only show bars with activity
  const runningBars = timelineData.map(segment =>
    (segment.status === 'running' && segment.production > 0) ? maxValue : null
  );
  const plannedBars = timelineData.map(segment =>
    segment.status === 'planned' ? maxValue : null
  );
  const unplannedBars = timelineData.map(segment =>
    segment.status === 'unplanned' ? maxValue : null
  );
  const downtimeBars = timelineData.map(segment =>
    segment.status === 'downtime' ? maxValue : null
  );

  const totalProduction = hourlyData.reduce((sum, item) => sum + item.totalCount, 0);
  const gap = targetData ? (totalProduction - targetData) : 0;

  const handleStoppageClick = (type) => {
    if (type === 'minor') {
      setModalData({
        title: 'Minor Stoppages (2+ minutes)',
        data: minorStoppages
      });
    } else {
      setModalData({
        title: 'Major Stoppages (5+ minutes)',
        data: majorStoppages
      });
    }
    setShowModal(true);
  };

  const { start: displayShiftStart, end: displayShiftEnd } = getShiftWindow();

  const chartOptions = {
    chart: {
      type: "column",
      height: 500,
      backgroundColor: 'transparent',
      spacing: [20, 20, 20, 20]
    },
    title: {
      // ✅ NEW — shows the actual selected shift timing, not a generic title
      text: `Production Timeline — Shift ${dashboardParams?.shift || 'A'} (${displayShiftStart} - ${displayShiftEnd})`,
      style: { fontSize: '22px', fontWeight: '700', color: '#2d3748' }
    },
    xAxis: {
      categories: timeLabels.map((lbl, i) => {
        if (i === 0) return `${lbl} (Start)`;
        if (i === timeLabels.length - 1) return `${lbl} (End)`;
        return lbl;
      }),
      title: {
        text: 'Shift Timing',
        style: { fontSize: '13px', fontWeight: '600', color: '#475569' }
      },
      gridLineWidth: 0,
      lineWidth: 2,
      lineColor: '#e2e8f0',
      tickWidth: 0,
      labels: {
        rotation: 0,
        style: { fontSize: '12px', fontWeight: '600', color: '#475569' },
        step: Math.max(1, Math.floor(timeLabels.length / 15))
      },
      min: 0,
      max: timeLabels.length - 1
    },
    yAxis: [
      {
        title: {
          text: "Production Status",
          style: { fontSize: '14px', fontWeight: '600' }
        },
        labels: { enabled: false },
        gridLineWidth: 0,
        opposite: false,
        visible: true
      },
      {
        title: {
          text: "",
          style: { fontSize: '14px', fontWeight: '700', color: '#2563eb' }
        },
        labels: {
          style: { fontSize: '13px', fontWeight: '700', color: '#2563eb' }
        },
        gridLineColor: '#f1f5f9',
        gridLineWidth: 1,
        opposite: true
      }
    ],
    plotOptions: {
      column: {
        stacking: "normal",
        borderWidth: 0,
        borderColor: 'transparent',
        pointPadding: 0,
        groupPadding: 0,
        pointPlacement: 'between'
      },
      line: {
        yAxis: 1,
        marker: {
          enabled: true,
          radius: 5,
          symbol: 'circle'
        },
        lineWidth: 4
      }
    },
    tooltip: {
      shared: true,
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      borderColor: '#2563eb',
      borderRadius: 12,
      borderWidth: 3,
      padding: 15,
      style: { fontSize: '13px', fontWeight: '600' },
      formatter: function() {
        const index = this.points[0].point.index;
        const segment = timelineData[index];

        const startLabel = formatTimeLabel(segment.start);
        const endLabel = formatTimeLabel(segment.end);

        let tooltip = `<b>${startLabel} - ${endLabel}</b><br/>`;

        if (segment.status === 'running') {
          tooltip += `<span style="color:#10b981">● Running</span><br/>`;
          tooltip += `Production: <b>${segment.production}</b><br/>`;
        } else if (segment.status === 'planned') {
          tooltip += `<span style="color:#ffc107">● Planned Downtime</span><br/>`;
          tooltip += `Reason: <b>${segment.reason}</b><br/>`;
          tooltip += `Duration: <b>${Math.round((segment.end - segment.start) * 60)} min</b><br/>`;
        } else if (segment.status === 'unplanned') {
          tooltip += `<span style="color:#f44336">● Unplanned Downtime</span><br/>`;
          tooltip += `Reason: <b>${segment.reason}</b><br/>`;
          tooltip += `Duration: <b>${Math.round((segment.end - segment.start) * 60)} min</b><br/>`;
        } else {
          tooltip += `<span style="color:#94a3b8">● Downtime</span><br/>`;
          tooltip += `Reason: <b>${segment.reason}</b><br/>`;
          tooltip += `Duration: <b>${Math.round((segment.end - segment.start) * 60)} min</b><br/>`;
        }

        const productionPoint = this.points.find(p => p.series.name === 'Cumulative Production');
        const targetPoint = this.points.find(p => p.series.name === 'Daily Target');

        if (productionPoint) tooltip += `Cumulative: <b>${productionPoint.y}</b><br/>`;
        if (targetPoint) tooltip += `Target: <b>${targetPoint.y}</b>`;

        return tooltip;
      }
    },
    legend: {
      enabled: true,
      align: 'center',
      verticalAlign: 'bottom',
      itemStyle: { fontSize: '13px', fontWeight: '600' }
    },
    series: [
      {
        name: "Running",
        type: "column",
        data: runningBars,
        color: "#10b981",
        borderWidth: 0,
        enableMouseTracking: true,
        yAxis: 0,
        zIndex: 1
      },
      {
        name: "Planned Downtime",
        type: "column",
        data: plannedBars,
        color: "#ffc107",
        borderWidth: 0,
        enableMouseTracking: true,
        yAxis: 0,
        zIndex: 1
      },
      {
        name: "Unplanned Downtime",
        type: "column",
        data: unplannedBars,
        color: "#f44336",
        borderWidth: 0,
        enableMouseTracking: true,
        yAxis: 0,
        zIndex: 1
      },
      {
        name: "Downtime",
        type: "column",
        data: downtimeBars,
        color: "#f7072f",
        borderWidth: 0,
        enableMouseTracking: true,
        yAxis: 0,
        zIndex: 1
      },
      ...(targetLineData ? [{
        name: "Daily Target",
        type: "line",
        data: targetLineData,
        color: '#4ade80',
        lineWidth: 3.5,
        dashStyle: 'Dash',
        marker: {
          enabled: false
        },
        dataLabels: {
          enabled: true,
          formatter: function () {
            if (this.point.index === this.series.data.length - 1) {
              return 'Daily Target';
            }
            return null;
          },
          align: 'left',
          verticalAlign: 'middle',
          style: {
            color: '#166534',
            fontWeight: '700',
            fontSize: '13px',
            textOutline: 'none'
          }
        },
        yAxis: 1,
        zIndex: 7
      }] : []),
      {
        name: "Cumulative Production",
        type: "line",
        data: cumulativeData,
        color: '#2563eb',
        lineWidth: 4,
        marker: {
          enabled: true,
          radius: 6,
          fillColor: '#2563eb',
          lineWidth: 2,
          lineColor: '#ffffff'
        },
        yAxis: 1,
        zIndex: 6
      }
    ]
  };

  const styles = {
    container: {
      padding: '30px',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e8edf2 100%)',
      minHeight: '100vh',
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
    },
    pageHeader: {
      background: 'white',
      padding: '25px 30px',
      borderRadius: '20px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
      marginBottom: '30px',
      border: '1px solid rgba(37, 99, 235, 0.1)'
    },
    pageTitle: {
      fontSize: '36px',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: '15px'
    },
    paramInfo: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '15px',
      marginTop: '15px'
    },
    paramBadge: {
      padding: '8px 16px',
      background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
      border: '2px solid #93c5fd',
      borderRadius: '8px',
      fontSize: '13px',
      fontWeight: '600',
      color: '#1e40af'
    },
    chartSection: {
      background: 'white',
      padding: '30px',
      borderRadius: '20px',
      marginBottom: '30px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
      border: '1px solid rgba(0, 0, 0, 0.05)'
    },
    summaryBar: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '20px',
      marginBottom: '30px'
    },
    summaryCard: {
      background: 'white',
      padding: '18px 20px',
      borderRadius: '12px',
      border: '2px solid',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s ease'
    },
    summaryCardTotal: {
      borderColor: '#10b981'
    },
    summaryCardTarget: {
      borderColor: '#3b82f6'
    },
    summaryCardGapPositive: {
      borderColor: '#10b981'
    },
    summaryCardGapNegative: {
      borderColor: '#ef4444'
    },
    summaryContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    summaryIcon: {
      width: '50px',
      height: '50px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '24px',
      flexShrink: 0
    },
    summaryText: {
      flex: 1
    },
    summaryLabel: {
      fontSize: '11px',
      fontWeight: '700',
      color: '#64748b',
      textTransform: 'uppercase',
      letterSpacing: '1px',
      marginBottom: '4px'
    },
    summaryValue: {
      fontSize: '26px',
      fontWeight: '800',
      lineHeight: 1.1,
      marginBottom: '2px'
    },
    summaryUnit: {
      fontSize: '11px',
      color: '#94a3b8',
      fontWeight: '600'
    },
    dataGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
      gap: '25px',
      marginBottom: '30px'
    },
    compactSection: {
      background: 'white',
      padding: '25px',
      borderRadius: '20px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
      border: '2px solid rgba(37, 99, 235, 0.1)',
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '500px'
    },
    sectionHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '20px',
      paddingBottom: '15px',
      borderBottom: '3px solid',
      borderImage: 'linear-gradient(90deg, #2563eb, #7c3aed) 1'
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '700',
      color: '#2d3748',
      margin: 0
    },
    sectionBadge: {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      padding: '4px 12px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '700'
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
      paddingRight: '10px'
    },
    downtimeItem: {
      padding: '12px 15px',
      background: 'linear-gradient(135deg, #f8f9ff 0%, #ffffff 100%)',
      borderRadius: '10px',
      marginBottom: '10px',
      borderLeft: '4px solid',
      transition: 'all 0.2s ease'
    },
    hourlyRow: {
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: '15px',
      padding: '14px 18px',
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
      borderRadius: '10px',
      marginBottom: '8px',
      border: '2px solid #f1f5f9',
      transition: 'all 0.2s ease',
      alignItems: 'center'
    },
    hourlyTime: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#475569'
    },
    hourlyCount: {
      fontSize: '20px',
      fontWeight: '800',
      color: '#2563eb',
      textAlign: 'right'
    },
    cardsSection: {
      background: 'white',
      padding: '30px',
      borderRadius: '20px',
      marginBottom: '30px',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
      border: '1px solid rgba(0, 0, 0, 0.05)'
    },
    cardsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '25px'
    },
    stoppageCard: {
      background: 'linear-gradient(135deg, #ffffff 0%, #f8f9ff 100%)',
      border: '2px solid transparent',
      padding: '25px',
      borderRadius: '16px',
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },
    cardHeader: {
      display: 'flex',
      gap: '18px',
      alignItems: 'center',
      marginBottom: '20px'
    },
    cardIcon: {
      width: '60px',
      height: '60px',
      borderRadius: '16px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: 'white',
      fontSize: '26px',
      boxShadow: '0 8px 20px rgba(0, 0, 0, 0.15)'
    },
    cardTitle: {
      fontSize: '15px',
      fontWeight: '600',
      color: '#718096',
      marginBottom: '6px',
      textTransform: 'uppercase'
    },
    cardValue: {
      fontSize: '32px',
      fontWeight: '800',
      color: '#2d3748'
    },
    kpiGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '25px',
      marginBottom: '30px'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modalContent: {
      background: 'white',
      padding: '30px',
      borderRadius: '20px',
      maxWidth: '600px',
      width: '90%',
      maxHeight: '80vh',
      overflow: 'auto'
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px'
    },
    closeButton: {
      background: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '8px 16px',
      cursor: 'pointer',
      fontWeight: '600'
    },
    stoppageItem: {
      padding: '15px',
      background: '#f8f9ff',
      borderRadius: '10px',
      marginBottom: '10px',
      borderLeft: '4px solid #2563eb'
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid #f1f5f9',
            borderTop: '4px solid #2563eb',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p style={{ fontSize: '18px', fontWeight: '600', color: '#64748b' }}>
            Loading dashboard data...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={{
          background: 'white',
          padding: '30px',
          borderRadius: '20px',
          textAlign: 'center'
        }}>
          <p style={{ color: '#ef4444', fontSize: '18px', fontWeight: '600' }}>
            {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {!modulesReady && <div>Loading dashboard...</div>}
      {modulesReady && dashboardParams && (
        <>
          <div style={styles.pageHeader}>
            <h1 style={styles.pageTitle}>Production Dashboard</h1>
            <div style={styles.paramInfo}>
              <span style={styles.paramBadge}>🏭 {dashboardParams.plantName}</span>
              <span style={styles.paramBadge}>🕐 Shift {dashboardParams.shift}</span>
              {/* ✅ NEW — shows the actual timing window being plotted */}
              <span style={styles.paramBadge}>⏱️ {displayShiftStart} - {displayShiftEnd}</span>
              <span style={styles.paramBadge}>📍 {dashboardParams.area}</span>
              <span style={styles.paramBadge}>📋 {dashboardParams.projectName}</span>
              <span style={styles.paramBadge}>⚙️ {dashboardParams.lineName}</span>
              <span style={styles.paramBadge}>👤 {dashboardParams.userName}</span>
              <span style={styles.paramBadge}>📅 {dashboardParams.startDate} to {dashboardParams.endDate}</span>
            </div>
          </div>

          <div style={styles.summaryBar}>
            <div style={{...styles.summaryCard, ...styles.summaryCardTotal}}>
              <div style={styles.summaryContent}>
                <div style={{...styles.summaryIcon, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'}}>
                  ✓
                </div>
                <div style={styles.summaryText}>
                  <div style={styles.summaryLabel}>TOTAL</div>
                  <div style={{...styles.summaryValue, color: '#10b981'}}>
                    {totalProduction.toLocaleString()}
                  </div>
                  <div style={styles.summaryUnit}>units</div>
                </div>
              </div>
            </div>

            <div style={{...styles.summaryCard, ...styles.summaryCardTarget}}>
              <div style={styles.summaryContent}>
                <div style={{...styles.summaryIcon, background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'}}>
                  🎯
                </div>
                <div style={styles.summaryText}>
                  <div style={styles.summaryLabel}>TARGET</div>
                  <div style={{...styles.summaryValue, color: '#3b82f6'}}>
                    {targetData ? targetData.toLocaleString() : 'N/A'}
                  </div>
                  <div style={styles.summaryUnit}>units</div>
                </div>
              </div>
            </div>

            <div style={{...styles.summaryCard, ...(gap >= 0 ? styles.summaryCardGapPositive : styles.summaryCardGapNegative)}}>
              <div style={styles.summaryContent}>
                <div style={{...styles.summaryIcon, background: gap >= 0 ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'}}>
                  {gap >= 0 ? '✓' : '✕'}
                </div>
                <div style={styles.summaryText}>
                  <div style={styles.summaryLabel}>GAP</div>
                  <div style={{...styles.summaryValue, color: gap >= 0 ? '#10b981' : '#ef4444'}}>
                    {gap >= 0 ? '+' : ''}{gap.toLocaleString()}
                  </div>
                  <div style={styles.summaryUnit}>units</div>
                </div>
              </div>
            </div>
          </div>

          <div style={styles.chartSection}>
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          </div>

          <div style={styles.dataGrid}>
            {getDowntimeEvents().length > 0 && (
              <div style={styles.compactSection}>
                <div style={styles.sectionHeader}>
                  <h3 style={styles.sectionTitle}>⚠️ Downtime Events</h3>
                  <span style={styles.sectionBadge}>{getDowntimeEvents().length}</span>
                </div>
                <div style={styles.scrollableContent}>
                  {getDowntimeEvents().map((dt, idx) => {
                    let color = '#94a3b8';
                    let icon = '⚫';

                    if (dt.status === 'planned') {
                      color = '#ffc107';
                      icon = '🟡';
                    } else if (dt.status === 'unplanned') {
                      color = '#f44336';
                      icon = '🔴';
                    }

                    return (
                      <div key={idx} style={{...styles.downtimeItem, borderLeftColor: color}}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                          <span>{icon}</span>
                          <strong style={{ color: '#1e293b', fontSize: '14px' }}>{dt.displayText}: {dt.reason}</strong>
                        </div>
                        <div style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>
                          {dt.timeRange} • {dt.durationMin} min
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div style={styles.compactSection}>
              <div style={styles.sectionHeader}>
                <h3 style={styles.sectionTitle}>⏰ Hourly Production</h3>
                <span style={styles.sectionBadge}>Shift {dashboardParams?.shift || 'A'} • {hourlyData.length} hrs</span>
              </div>
              <div style={styles.scrollableContent}>
                {hourlyData.map((row, i) => (
                  <div key={i} style={styles.hourlyRow}>
                    <div style={styles.hourlyTime}>
                      {new Date(row.hourWindow).toLocaleString('en-US', {
                        month: 'short',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: true
                      })}
                    </div>
                    <div style={styles.hourlyCount}>
                      {row.totalCount.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={styles.cardsSection}>
            <h3 style={{fontSize: '20px', fontWeight: '700', color: '#2d3748', marginBottom: '25px'}}>Production Summary</h3>
            <div style={styles.cardsGrid}>
              <div style={styles.stoppageCard}>
                <div style={styles.cardHeader}>
                  <div style={{...styles.cardIcon, background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'}}>
                    💡
                  </div>
                  <div style={{flex: 1}}>
                    <div style={styles.cardTitle}>Total Production</div>
                    <div style={styles.cardValue}>{totalProduction}</div>
                  </div>
                </div>
              </div>

              <div
                style={{...styles.stoppageCard, cursor: 'pointer'}}
                onClick={() => handleStoppageClick('minor')}
              >
                <div style={styles.cardHeader}>
                  <div style={{...styles.cardIcon, background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
                    ⚠
                  </div>
                  <div style={{flex: 1}}>
                    <div style={styles.cardTitle}>Minor Stoppages</div>
                    <div style={styles.cardValue}>{minorStoppages.length}</div>
                  </div>
                </div>
                <div style={{
                  marginTop: '15px',
                  padding: '10px',
                  background: 'rgba(245, 158, 11, 0.1)',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#d97706',
                  textAlign: 'center'
                }}>
                  Click to view details →
                </div>
              </div>

              <div
                style={{...styles.stoppageCard, cursor: 'pointer'}}
                onClick={() => handleStoppageClick('major')}
              >
                <div style={styles.cardHeader}>
                  <div style={{...styles.cardIcon, background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'}}>
                    ⛔
                  </div>
                  <div style={{flex: 1}}>
                    <div style={styles.cardTitle}>Major Stoppages</div>
                    <div style={styles.cardValue}>{majorStoppages.length}</div>
                  </div>
                </div>
                <div style={{
                  marginTop: '15px',
                  padding: '10px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#dc2626',
                  textAlign: 'center'
                }}>
                  Click to view details →
                </div>
              </div>
            </div>
          </div>

          <div style={styles.kpiGrid}>
            <GaugeCard title="OPE" value={94} color="#8e24aa" />
            <GaugeCard title="OEE" value={94} color="#e65100" />
            <GaugeCard title="Availability" value={100} color="#f9a825" />
            <GaugeCard title="Quality" value={97} color="#2e7d32" />
            <GaugeCard title="Performance" value={96.9} color="#0277bd" />
          </div>

          {showModal && (
            <div style={styles.modal} onClick={() => setShowModal(false)}>
              <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <div style={styles.modalHeader}>
                  <h3 style={{ margin: 0, color: '#2d3748' }}>{modalData.title}</h3>
                  <button
                    style={styles.closeButton}
                    onClick={() => setShowModal(false)}
                  >
                    Close
                  </button>
                </div>
                <div>
                  {modalData.data.length > 0 ? (
                    modalData.data.map((item, idx) => (
                      <div key={idx} style={styles.stoppageItem}>
                        <div style={{ fontWeight: '700', marginBottom: '5px', color: '#2563eb' }}>
                          {item.timeRange}
                        </div>
                        <div style={{ fontSize: '14px', color: '#64748b' }}>
                          Duration: <strong>{item.gapMinutes} minutes</strong>
                        </div>
                        <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '5px' }}>
                          Date: {item.dayLabel}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p style={{ textAlign: 'center', color: '#94a3b8', padding: '20px' }}>
                      No stoppages recorded
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Dashboard;